import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import stripeApiService from '../../../services/stripe-subscription.service';
import PlanAndBilling from './PlanAndBilling';
import PageLoader from '../../../components/PageLoader/PageLoader';
import { delay } from '../../../utils/helpers';

// Helper: parse ISO 8601 trial_end into days remaining
const getDaysRemaining = (trialEnd) => {
  if (!trialEnd) return 0;
  const end = new Date(trialEnd);
  if (isNaN(end.getTime())) return 0;
  const diff = end - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const PlanAndBillingContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organizationData, isSuperAdmin, orgId, refreshOrganizationData } =
    useOutletContext();

  // State
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [cardDetails, setCardDetails] = useState(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState(true);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [
    subscriptionExpiredorPaymentFailed,
    setSubscriptionExpiredorPaymentFailed,
  ] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Unified loading state - page is ready when ALL data is loaded
  const [isPageReady, setIsPageReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Track first-ever ready state so refreshes don't unmount PlanAndBilling
  const hasEverBeenReady = useRef(false);

  // UI state
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isMakePaymentModalOpen, setIsMakePaymentModalOpen] = useState(false);
  const [isFrequencyModalOpen, setIsFrequencyModalOpen] = useState(false);
  const [isProcessingSubscriptionChange, setIsProcessingSubscriptionChange] =
    useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const triggerAction = query.get('trigger');

    if (triggerAction === 'changePlan') {
      refreshOrganizationData(); // Call the function

      // Remove query parameter from the URL without reloading the page
      const newUrl = location.pathname; // Get base URL without query params
      navigate(newUrl, { replace: true }); // Replace history entry to remove query
    }
  }, [location, navigate]);

  // Set subscription details when organization data changes
  useEffect(() => {
    if (!organizationData) {
      setIsPageReady(false);
      return;
    }

    // Redirect if force allow is set
    if (organizationData.sub_force_allow === true) {
      navigate('/landing');
      return;
    }

    // Build subscriptionDetails from flat fields for child components
    setSubscriptionDetails({
      id: organizationData.stripe_subscription_id,
      status: organizationData.status,
      plan_name: organizationData.plan_name,
      plan_id: organizationData.plan_id,
      billing_interval: organizationData.billing_interval,
      current_period_end: organizationData.current_period_end,
      trial_end: organizationData.trial_end,
    });
    setSubscriptionActive(organizationData.status !== 'paused');

    const hasSubscriptionIssues = organizationData.status === 'past_due' ||
      organizationData.status === 'canceled';

    setSubscriptionExpiredorPaymentFailed(hasSubscriptionIssues);

    // Load ALL required data before marking page as ready.
    // Only show the full-page loader on the very first load — subsequent
    // refreshes (triggered by refreshOrganizationData) must NOT call
    // setIsPageReady(false) because that unmounts PlanAndBilling and
    // destroys all local modal state (e.g. SavedCards auto-reopen).
    const loadAllData = async () => {
      if (!hasEverBeenReady.current) {
        setIsPageReady(false);
      }
      setLoadError(null);

      try {
        // Fetch billing and payment history in parallel
        await Promise.all([
          fetchBillingDetails(),
          fetchPaymentHistory()
        ]);

        hasEverBeenReady.current = true;
        setIsPageReady(true);
      } catch (error) {
        console.error('Error loading billing data:', error);
        setLoadError('Failed to load billing information');
        if (!hasEverBeenReady.current) {
          setIsPageReady(false);
        }
      }
    };

    loadAllData();
  }, [organizationData]);

  // Fetch billing details
  const fetchBillingDetails = async () => {
    if (
      !organizationData?.stripe_subscription_id ||
      !organizationData?.stripe_customer_id
    ) {
      setIsLoadingBilling(false);
      return;
    }

    try {
      setIsLoadingBilling(true);
      const billingDetailsData = await stripeApiService.getBillingDetails(
        organizationData.stripe_subscription_id,
        organizationData.stripe_customer_id,
      );

      setBillingDetails(billingDetailsData);
      setCardDetails(billingDetailsData?.card_details[0]);
    } catch (error) {
      toast.error('Error in fetching subscription details');
      console.error(error);
    } finally {
      setIsLoadingBilling(false);
    }
  };

  // Fetch payment history
  const fetchPaymentHistory = async (search = '') => {
    if (!organizationData?.stripe_customer_id) {
      setIsLoadingPayments(false);
      return;
    }

    try {
      setIsLoadingPayments(true);
      const { transaction_history } = await stripeApiService.getPaymentHistory(
        organizationData.stripe_customer_id,
        search,
      );

      setPaymentHistory(transaction_history);
    } catch (error) {
      toast.error('Error in fetching payment history');
      console.error(error);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Handle subscription toggle (pause/resume)
  const handleToggleSubscription = async () => {
    setIsProcessingSubscriptionChange(true);

    try {
      if (subscriptionActive) {
        await stripeApiService.pauseSubscription(
          organizationData.stripe_customer_id,
          organizationData.user_name,
        );
      } else {
        await stripeApiService.resumeSubscription(
          organizationData.stripe_customer_id,
          organizationData.user_name,
        );
      }
      await delay(3000);
      await refreshOrganizationData();
      setIsSubscriptionModalOpen(false);

      toast.success(
        `Subscription successfully ${subscriptionActive ? 'paused' : 'activated'}`,
      );
    } catch (error) {
      toast.error(
        `Failed to ${subscriptionActive ? 'cancel' : 'activate'} subscription`,
      );
    } finally {
      setIsProcessingSubscriptionChange(false);
    }
  };

  // Handle make payment
  const handelMakePayment = async () => {
    setIsProcessingPayment(true);

    try {
      await stripeApiService.makePaymentforSubscription(
        organizationData.stripe_customer_id,
      );

      await delay(3000);
      await refreshOrganizationData();
      setIsMakePaymentModalOpen(false);

      toast.success(`Payment confirmed successfully.`);
    } catch (error) {
      const errorMessage = error.response?.body
        ? JSON.parse(error.response.body).error
        : `Failed to make payment for the subscription.`;
      toast.error(errorMessage);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Search payment history
  const handleSearch = () => {
    fetchPaymentHistory(searchTerm);
  };

  // Search payment history
  const handleClearSearch = () => {
    setSearchTerm('');
    fetchPaymentHistory('');
  };

  // Handle payment method change
  const handlePaymentMethodChanged = (stayOpen) => {
    refreshOrganizationData();
    setIsPaymentMethodModalOpen(stayOpen);
  };

  //Handle delete payment method
  const handleDeletePaymentMethod = () => {
    fetchBillingDetails();
    setIsPaymentMethodModalOpen(false);
  };

  // Handle frequency change
  const handleFrequencyChanged = () => {
    refreshOrganizationData();
    setIsFrequencyModalOpen(false);
  };

  // Show error state if data failed to load
  if (loadError) {
    return (
      <div className="billing-container">
        <div className="error-state" style={{ 
          padding: '40px', 
          textAlign: 'center',
          background: 'var(--card-bg, #ffffff)',
          border: '1px solid var(--border-grey, #e5e7eb)',
          borderRadius: '8px'
        }}>
          <p style={{ color: 'var(--error-color, #dc2626)', marginBottom: '20px' }}>
            {loadError}
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show unified loading state until ALL data is ready
  if (!isPageReady) {
    return <PageLoader message="Loading billing information..." />;
  }

  // Free trial view — skip all Stripe UI, show countdown + activation CTA
  if (subscriptionDetails?.status === 'free_trial') {
    const daysRemaining = getDaysRemaining(subscriptionDetails?.trial_end);
    const daysUsed = 14 - daysRemaining;
    const progressPct = Math.min(100, Math.round((daysUsed / 14) * 100));

    return (
      <div style={{ maxWidth: '520px', margin: '40px auto', textAlign: 'center' }}>
        <div
          style={{
            background: 'var(--card-bg, #ffffff)',
            border: '1px solid var(--border-grey, #e5e7eb)',
            borderRadius: '12px',
            padding: '36px 32px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              background: '#eff6ff',
              color: '#2563eb',
              borderRadius: '20px',
              padding: '4px 14px',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '20px',
            }}
          >
            Free Trial
          </span>
          <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '6px' }}>
            {daysRemaining > 0
              ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
              : 'Your free trial has ended'}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
            {daysRemaining > 0
              ? 'Enjoy full access during your 14-day free trial. No credit card required until you activate.'
              : 'Activate your subscription to continue using PrescriptSure.'}
          </p>

          {/* Progress bar */}
          <div
            style={{
              background: '#e5e7eb',
              borderRadius: '999px',
              height: '8px',
              marginBottom: '8px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                background: daysRemaining <= 3 ? '#ef4444' : '#2563eb',
                height: '100%',
                borderRadius: '999px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '28px' }}>
            Day {Math.min(daysUsed, 14)} of 14
          </p>

          <button
            className="btn btn-primary"
            style={{ width: '100%', padding: '10px 0', fontWeight: 600 }}
            onClick={() => navigate('/subscription')}
          >
            Activate Subscription
          </button>
        </div>
      </div>
    );
  }

  return (
    <PlanAndBilling
      // Organization data
      organizationData={organizationData}
      isSuperAdmin={isSuperAdmin}
      orgId={orgId}
      // Subscription data
      subscriptionDetails={subscriptionDetails}
      billingDetails={billingDetails}
      cardDetails={cardDetails}
      paymentHistory={paymentHistory}
      subscriptionActive={subscriptionActive}
      subscriptionExpiredorPaymentFailed={subscriptionExpiredorPaymentFailed}
      // Loading states
      isLoadingBilling={isLoadingBilling}
      isLoadingPayments={isLoadingPayments}
      isProcessingSubscriptionChange={isProcessingSubscriptionChange}
      isProcessingPayment={isProcessingPayment}
      // Modal states
      isPaymentMethodModalOpen={isPaymentMethodModalOpen}
      setIsPaymentMethodModalOpen={setIsPaymentMethodModalOpen}
      isSubscriptionModalOpen={isSubscriptionModalOpen}
      setIsSubscriptionModalOpen={setIsSubscriptionModalOpen}
      isFrequencyModalOpen={isFrequencyModalOpen}
      setIsFrequencyModalOpen={setIsFrequencyModalOpen}
      isMakePaymentModalOpen={isMakePaymentModalOpen}
      setIsMakePaymentModalOpen={setIsMakePaymentModalOpen}
      // Search state
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      // Event handlers
      handleToggleSubscription={handleToggleSubscription}
      handleSearch={handleSearch}
      handleClearSearch={handleClearSearch}
      handlePaymentMethodChanged={handlePaymentMethodChanged}
      handleFrequencyChanged={handleFrequencyChanged}
      handleDeletePaymentMethod={handleDeletePaymentMethod}
      handleMakePayment={handelMakePayment}
    />
  );
};

export default PlanAndBillingContainer;
